import React, { Fragment } from 'react'
import { ILogger } from '@wix/thunderbolt-symbols'

type Props = {
	id: string
	logger: ILogger
	Component?: React.ComponentType<any>
	compClassType: string
	recursiveChildren: (itemId?: string) => Array<JSX.Element>
}

type State = {
	hasError: boolean
}

export class MissingCompError extends Error {
	constructor(componentType: string) {
		super(`${componentType} Component is missing in editor-elements`)

		if (typeof Error.captureStackTrace === 'function') {
			Error.captureStackTrace(this, this.constructor)
		} else {
			this.stack = new Error(this.message).stack
		}
	}
}

export const DeadComp = ({ id, children }: { id: string; children: React.ReactNode }) => (
	<div data-dead-comp={true} id={id}>
		{children}
	</div>
)

export class ErrorBoundary extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props)
		const { Component, compClassType } = props
		this.state = { hasError: !Component }
		if (!Component) {
			this.captureError(new MissingCompError(compClassType))
		}
	}

	static getDerivedStateFromError() {
		return { hasError: true }
	}

	private captureError(error: Error, componentStack: string = '') {
		this.props.logger.captureError(error, {
			tags: { componentType: this.props.compClassType, componentName: 'dead-component' },
			extras: { componentStack },
		})
	}

	componentDidCatch(error: Error, { componentStack }: React.ErrorInfo) {
		this.captureError(error, componentStack)
	}

	render() {
		if (this.state.hasError) {
			/* TODO: remove recursiveChildren when we implement all components.
				dead comp shouldn't render it's children - potential bug
			 */
			return <DeadComp id={this.props.id}>{this.props.recursiveChildren()}</DeadComp>
		}

		return <Fragment>{this.props.children}</Fragment>
	}
}
